import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowUpCircle, ArrowDownCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const SwapHistory = ({ tokenAddress, tokenSymbol }) => {
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [newSwapIds, setNewSwapIds] = useState(new Set()); // Track new swaps
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [showOnlyOurs, setShowOnlyOurs] = useState(false); // Filter toggle
  
  // Calculate stats for our wallets
  const ourSwaps = swaps.filter(s => s.is_our_wallet);
  const ourBuys = ourSwaps.filter(s => s.type === 'buy').length;
  const ourSells = ourSwaps.filter(s => s.type === 'sell').length;
  
  // Filter swaps based on toggle
  const displayedSwaps = showOnlyOurs ? ourSwaps : swaps;

  useEffect(() => {
    loadSwaps();

    if (autoRefresh) {
      const interval = setInterval(() => {
        loadSwaps(true); // Silent refresh
      }, 3000); // Refresh every 3 seconds (faster!)

      return () => clearInterval(interval);
    }
  }, [tokenAddress, autoRefresh]);

  const loadSwaps = async (silent = false) => {
    if (!tokenAddress) return;
    
    if (!silent) setLoading(true);
    
    try {
      const response = await axios.get(
        `${API_URL}/api/token/${tokenAddress}/swaps`,
        {
          params: { 
            limit: 50,
            timestamp: Date.now() // Cache busting parameter
          }
        }
      );

      const swapData = response.data.swaps || [];
      
      if (swapData.length === 0) {
        // AUDIT 2026-07-02: раньше здесь генерились СЛУЧАЙНЫЕ фейковые свопы
        // (generateMockSwaps) — оператор видел несуществующие сделки. Пустой ответ =
        // пусто; ниже есть корректный empty-state «No swap history available».
        setSwaps([]);
      } else {
        // Detect new swaps
        if (silent && swaps.length > 0) {
          const existingHashes = new Set(swaps.map(s => s.tx_hash || s.timestamp));
          const newOnes = new Set();
          
          swapData.forEach(swap => {
            const swapId = swap.tx_hash || swap.timestamp;
            if (!existingHashes.has(swapId)) {
              newOnes.add(swapId);
              
              // Show toast for our wallet swaps
              if (swap.is_our_wallet) {
                toast.success(`New ${swap.type} detected!`, {
                  description: `${swap.amount.toLocaleString()} tokens at $${swap.price.toFixed(8)}`
                });
              }
            }
          });
          
          if (newOnes.size > 0) {
            setNewSwapIds(newOnes);
            // Clear new swap highlights after 5 seconds
            setTimeout(() => setNewSwapIds(new Set()), 5000);
          }
        }
        
        setSwaps(swapData);
        setLastUpdateTime(Date.now());
      }

    } catch (error) {
      console.error('Error loading swaps:', error);
      if (!silent) {
        toast.error('Failed to load swap history');
      }
      // AUDIT 2026-07-02: раньше при ошибке показывались фейковые свопы. Транзиентная
      // ошибка поллинга не должна ни рисовать фейки, ни затирать уже загруженные данные —
      // просто оставляем текущее состояние.
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return price.toFixed(8);
  };

  const formatAmount = (amount) => {
    return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between mb-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              Swap History
              {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Showing {tokenSymbol || 'token'} amount and price in USD
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              Updated: {new Date(lastUpdateTime).toLocaleTimeString()}
            </span>
            <Badge variant={autoRefresh ? 'default' : 'secondary'}>
              {autoRefresh ? 'Live (3s)' : 'Paused'}
            </Badge>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {autoRefresh ? 'Pause' : 'Resume'}
            </button>
          </div>
        </div>
        
        {/* Stats and Filter */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Total Swaps:</span>
              <Badge variant="outline">{swaps.length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Our Wallets:</span>
              <Badge variant="default" className="bg-blue-600">{ourSwaps.length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600 font-medium">{ourBuys} buys</span>
            </div>
            <div className="flex items-center gap-2">
              <ArrowDownCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-600 font-medium">{ourSells} sells</span>
            </div>
          </div>
          
          <button
            onClick={() => setShowOnlyOurs(!showOnlyOurs)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              showOnlyOurs 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showOnlyOurs ? '✓ Our Wallets Only' : 'Show All'}
          </button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Token Price (USD)</TableHead>
                <TableHead>Token Amount</TableHead>
                <TableHead>Volume (USD)</TableHead>
                <TableHead>Wallet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedSwaps.map((swap, index) => {
                const swapId = swap.tx_hash || swap.timestamp;
                const isNew = newSwapIds.has(swapId);
                const isOurWallet = swap.is_our_wallet;
                
                // Different styling for our wallets
                let rowClass = 'transition-all duration-300';
                if (isNew) {
                  rowClass += ' animate-pulse bg-yellow-100';
                } else if (isOurWallet) {
                  // Our wallet with type-specific colors
                  if (swap.type === 'buy') {
                    rowClass += ' bg-green-50 border-l-4 border-green-500';
                  } else {
                    rowClass += ' bg-red-50 border-l-4 border-red-500';
                  }
                } else {
                  rowClass += ' hover:bg-gray-50';
                }
                
                return (
                  <TableRow 
                    key={index}
                    className={rowClass}
                  >
                    <TableCell className="font-mono text-xs">
                      {isNew && (
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-ping"></span>
                      )}
                      {formatTime(swap.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {swap.type === 'buy' ? (
                          <>
                            <ArrowUpCircle className={`h-4 w-4 ${isOurWallet ? 'text-green-600' : 'text-green-500'}`} />
                            <span className={`font-medium ${isOurWallet ? 'text-green-700' : 'text-green-600'}`}>
                              {isOurWallet ? 'BUY ✓' : 'Buy'}
                            </span>
                          </>
                        ) : (
                          <>
                            <ArrowDownCircle className={`h-4 w-4 ${isOurWallet ? 'text-red-600' : 'text-red-500'}`} />
                            <span className={`font-medium ${isOurWallet ? 'text-red-700' : 'text-red-600'}`}>
                              {isOurWallet ? 'SELL ✓' : 'Sell'}
                            </span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      ${formatPrice(swap.price)}
                    </TableCell>
                    <TableCell className={`font-medium ${isOurWallet ? 'text-gray-900' : 'text-gray-700'}`}>
                      {formatAmount(swap.amount)}
                    </TableCell>
                    <TableCell className={isOurWallet ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                      ${swap.volume_usd.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-xs ${isOurWallet ? 'font-bold' : ''}`}>
                          {formatAddress(swap.wallet_address)}
                        </span>
                        {isOurWallet && (
                          <Badge 
                            variant="default" 
                            className={`text-xs ${
                              swap.type === 'buy' 
                                ? 'bg-green-600 hover:bg-green-700' 
                                : 'bg-red-600 hover:bg-red-700'
                            }`}
                          >
                            OUR {swap.type.toUpperCase()}
                          </Badge>
                        )}
                        {isNew && (
                          <Badge variant="secondary" className="text-xs bg-yellow-200 text-yellow-800 font-bold">
                            NEW
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {displayedSwaps.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              {showOnlyOurs ? 'No swaps from our wallets yet' : 'No swap history available'}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default SwapHistory;
